import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { AppConfigService } from '../../config/app-config.service';

/**
 * Shared state for the authenticated user's profile image.
 *
 * Owns exactly one blob URL for the current profile picture and exposes it as a
 * signal so both the profile-settings page (via ProfileImageUploaderComponent)
 * and the top navbar can subscribe to the same value. Without a shared source
 * both consumers used to fire their own `GET /users/me/profile-image` request
 * — one showing a spinner, the other silently refreshing — which is why the
 * two spots updated at different times after an upload.
 */
@Injectable({ providedIn: 'root' })
export class ProfileImageState {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _imageUrl = signal<string | null>(null);
  private readonly _loading = signal(false);

  /** Blob URL for the current profile image, or null if the user has none. */
  public readonly imageUrl = this._imageUrl.asReadonly();
  /** True only while the initial load fetch is in flight. */
  public readonly loading = this._loading.asReadonly();

  /**
   * User id the current cached image belongs to. Kept so a login as a different
   * user (SPA flow without a hard reload) doesn't reuse the previous user's
   * cached blob — ensureLoaded refetches whenever this key changes.
   */
  private _initializedForUserId: string | null = null;

  constructor() {
    this._destroyRef.onDestroy(() => this._revokeCurrentUrl());
  }

  /**
   * Fetches the current profile image for the given user. Idempotent for the
   * same userId — repeated calls with the same id are a no-op. If the userId
   * changes (SPA login flip), the cached URL is dropped and a fresh fetch
   * runs. This path shows a spinner (loading = true) because the consumer has
   * nothing to display yet.
   */
  public ensureLoaded(userId: string): void {
    if (this._initializedForUserId === userId) return;
    this._initializedForUserId = userId;
    this._revokeCurrentUrl();
    this._imageUrl.set(null);
    this._fetch({ showLoading: true, clearOnError: true });
  }

  /**
   * Re-fetches the profile image after an upload.
   */
  public refresh(): void {
    this._fetch({ showLoading: false, clearOnError: false });
  }

  /**
   * Clears the cached image and initialization key. Call on logout so the next
   * login doesn't inherit the previous user's avatar. Safe to call multiple times.
   */
  public clear(): void {
    this._revokeCurrentUrl();
    this._imageUrl.set(null);
    this._loading.set(false);
    this._initializedForUserId = null;
  }

  private _fetch(options: { showLoading: boolean; clearOnError: boolean }): void {
    if (options.showLoading) {
      this._loading.set(true);
    }
    // Cache-buster: GET /users/me/profile-image doesn't emit Cache-Control headers,
    // and browsers (specifically in QA/prod, where the dev server no-cache defaults
    // aren't in play) end up serving the previous response from the disk cache after
    // an upload. The image bytes never change per URL, so the <img> stays on the
    // old blob even though we created a fresh objectURL. A unique query param on
    // every call sidesteps the cache without needing a backend header change.
    const requestUrl = `${this._config.versionedApiUrl}/users/me/profile-image?_=${Date.now()}`;
    this._http
      .get(requestUrl, { responseType: 'blob' })
      .subscribe({
        next: (blob) => {
          if (!(blob instanceof Blob) || blob.size === 0) {
            // Backend says the user has no image. Clear whatever we had.
            this._revokeCurrentUrl();
            this._imageUrl.set(null);
            this._loading.set(false);
            return;
          }
          const url = URL.createObjectURL(blob);
          this._revokeCurrentUrl();
          this._imageUrl.set(url);
          this._loading.set(false);
        },
        error: () => {
          // Only blank the avatar on the initial load. A failed refresh (network
          // hiccup after an upload) keeps the previous image visible.
          if (options.clearOnError) {
            this._revokeCurrentUrl();
            this._imageUrl.set(null);
          }
          this._loading.set(false);
        },
      });
  }

  private _revokeCurrentUrl(): void {
    const current = this._imageUrl();
    if (current) {
      URL.revokeObjectURL(current);
    }
  }
}
